/**
 * Error Boundary Component for Project Lighthouse
 * Catches and handles React component errors with user-friendly fallbacks
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Stack,
  Chip,
  Divider,
  Collapse
} from '@mui/material';
import {
  ErrorOutline,
  Refresh,
  BugReport,
  ExpandMore,
  ExpandLess,
  Home
} from '@mui/icons-material';
import { errorHandler, FrontendError } from '../services/errorHandler';

// =============================================================================
// Error Boundary Types
// =============================================================================

interface ErrorBoundaryProps {
  children: ReactNode;
  componentName?: string;
  fallback?: ReactNode;
  showErrorDetails?: boolean;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  frontendError: FrontendError | null;
  showDetails: boolean;
  retryCount: number;
}

// =============================================================================
// Error Boundary Component
// =============================================================================

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private maxRetries = 3;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      frontendError: null,
      showDetails: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Handle the error using our centralized error handler
    const frontendError = errorHandler.handleComponentError(
      error,
      { componentStack: errorInfo.componentStack || '' },
      this.props.componentName
    );

    this.setState({
      errorInfo,
      frontendError
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Reset error state when children change (for retry functionality)
    if (this.state.hasError && prevProps.children !== this.props.children) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        frontendError: null,
        showDetails: false
      });
    }
  }

  private handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        frontendError: null,
        showDetails: false,
        retryCount: prevState.retryCount + 1
      }));
    } else {
      // Max retries reached, suggest page refresh
      window.location.reload();
    }
  };

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private toggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails
    }));
  };

  private handleReportBug = () => {
    // In a real implementation, this would:
    // - Open a bug report form
    // - Send error details to support system
    // - Create a support ticket
    console.log('Bug report for error:', this.state.error);
    
    // For now, just show instructions
    alert('To report this bug:\n\n1. Take a screenshot of this error\n2. Email support@vitaldynamix.com\n3. Include the error details shown below');
  };

  render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo, frontendError, showDetails, retryCount } = this.state;
      const canRetry = retryCount < this.maxRetries;
      const isProduction = process.env.NODE_ENV === 'production';

      return (
        <Box
          sx={{
            p: 3,
            minHeight: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Card
            sx={{
              maxWidth: 600,
              width: '100%',
              border: '2px solid',
              borderColor: 'error.main',
              borderRadius: 2
            }}
          >
            <CardContent sx={{ p: 3 }}>
              {/* Header */}
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <ErrorOutline color="error" sx={{ fontSize: 32 }} />
                <Box>
                  <Typography variant="h5" color="error" gutterBottom>
                    Oops! Something went wrong
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {frontendError?.userMessage || 'An unexpected error occurred while loading this content.'}
                  </Typography>
                </Box>
              </Stack>

              {/* Error Summary */}
              {frontendError && (
                <Alert 
                  severity={frontendError.severity === 'critical' || frontendError.severity === 'high' ? 'error' : 'warning'}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    <strong>Error Code:</strong> {frontendError.code}
                  </Typography>
                  {frontendError.component && (
                    <Typography variant="body2">
                      <strong>Component:</strong> {frontendError.component}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    <strong>Time:</strong> {frontendError.timestamp.toLocaleString()}
                  </Typography>
                </Alert>
              )}

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                {canRetry && (
                  <Button
                    variant="contained"
                    startIcon={<Refresh />}
                    onClick={this.handleRetry}
                    color="primary"
                  >
                    Try Again ({this.maxRetries - retryCount} left)
                  </Button>
                )}
                
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={this.handleRefresh}
                >
                  Refresh Page
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<Home />}
                  onClick={this.handleGoHome}
                >
                  Go Home
                </Button>
              </Stack>

              {/* Recovery Suggestions */}
              {frontendError && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    What you can try:
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    <li>
                      <Typography variant="body2">
                        Refresh the page or try again in a few moments
                      </Typography>
                    </li>
                    {frontendError.retryable && (
                      <li>
                        <Typography variant="body2">
                          Check your internet connection
                        </Typography>
                      </li>
                    )}
                    <li>
                      <Typography variant="body2">
                        Clear your browser cache and cookies
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        Contact support if the problem persists
                      </Typography>
                    </li>
                  </ul>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Report Bug */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Button
                  variant="text"
                  startIcon={<BugReport />}
                  onClick={this.handleReportBug}
                  size="small"
                >
                  Report Bug
                </Button>

                {/* Error Details Toggle */}
                {!isProduction && (
                  <Button
                    variant="text"
                    endIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
                    onClick={this.toggleDetails}
                    size="small"
                    aria-expanded={showDetails}
                    aria-controls="error-technical-details"
                    aria-label={`${showDetails ? 'Hide' : 'Show'} technical error details for debugging`}
                    id="error-details-toggle"
                  >
                    {showDetails ? 'Hide' : 'Show'} Technical Details
                  </Button>
                )}
              </Stack>

              {/* Technical Details (Development Only) */}
              {!isProduction && (
                <Collapse 
                  in={showDetails}
                  id="error-technical-details"
                  aria-labelledby="error-details-toggle"
                  role="region"
                  aria-hidden={!showDetails}
                >
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Technical Details:
                    </Typography>
                    
                    <Stack spacing={1}>
                      <Chip
                        label={`Error: ${error?.name || 'Unknown'}`}
                        size="small"
                        variant="outlined"
                        color="error"
                      />
                      
                      {frontendError?.code && (
                        <Chip
                          label={`Code: ${frontendError.code}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      
                      <Chip
                        label={`Component: ${this.props.componentName || 'Unknown'}`}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>

                    {error?.message && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" display="block" gutterBottom>
                          Error Message:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            p: 1,
                            bgcolor: 'error.light',
                            color: 'error.contrastText',
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            wordBreak: 'break-word'
                          }}
                        >
                          {error.message}
                        </Typography>
                      </Box>
                    )}

                    {error?.stack && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" display="block" gutterBottom>
                          Stack Trace:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            p: 1,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            whiteSpace: 'pre-wrap',
                            maxHeight: '200px',
                            overflow: 'auto',
                            wordBreak: 'break-word'
                          }}
                        >
                          {error.stack}
                        </Typography>
                      </Box>
                    )}

                    {errorInfo?.componentStack && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" display="block" gutterBottom>
                          Component Stack:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            p: 1,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            whiteSpace: 'pre-wrap',
                            maxHeight: '150px',
                            overflow: 'auto',
                            wordBreak: 'break-word'
                          }}
                        >
                          {errorInfo.componentStack}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              )}
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

// =============================================================================
// Higher-Order Component for Easy Error Boundary Wrapping
// =============================================================================

export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  componentName?: string,
  fallback?: ReactNode
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary componentName={componentName} fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
}

// =============================================================================
// Specialized Error Boundaries
// =============================================================================

/**
 * Error boundary for critical application components
 */
export const CriticalErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary
    componentName="CriticalComponent"
    showErrorDetails={true}
    fallback={
      <Alert severity="error" sx={{ m: 2 }}>
        <Typography variant="h6" gutterBottom>
          Critical System Error
        </Typography>
        <Typography variant="body2">
          A critical error has occurred. Please refresh the page or contact support immediately.
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Refresh Page
        </Button>
      </Alert>
    }
  >
    {children}
  </ErrorBoundary>
);

/**
 * Error boundary for form components
 */
export const FormErrorBoundary: React.FC<{ children: ReactNode; formName?: string }> = ({ 
  children, 
  formName 
}) => (
  <ErrorBoundary
    componentName={`Form-${formName}`}
    fallback={
      <Alert severity="warning" sx={{ m: 1 }}>
        <Typography variant="body2">
          There was a problem loading this form. Please try refreshing the page.
        </Typography>
        <Button
          size="small"
          onClick={() => window.location.reload()}
          sx={{ mt: 1 }}
        >
          Refresh
        </Button>
      </Alert>
    }
  >
    {children}
  </ErrorBoundary>
);

/**
 * Error boundary for optional/non-critical components
 */
export const OptionalErrorBoundary: React.FC<{ children: ReactNode; componentName?: string }> = ({ 
  children, 
  componentName 
}) => (
  <ErrorBoundary
    componentName={componentName}
    fallback={
      <Box sx={{ p: 1, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Content temporarily unavailable
        </Typography>
      </Box>
    }
  >
    {children}
  </ErrorBoundary>
);

export default ErrorBoundary;